import { useCallback, useEffect, useState } from 'react';

import type { UseAsyncDataResult } from './useAsyncData.interface.js';

export type { UseAsyncDataResult } from './useAsyncData.interface.js';

export interface UseAsyncDataOptions {
  /** Fallback message used when a rejection is not an `Error`. */
  errorMessage?: string;
}

/**
 * Shared primitive for "fetch one resource into `{ data, isLoading, error }`"
 * hooks. Pass `null` as the fetcher to stay idle (no request, empty result);
 * pass a memoized `(signal) => Promise<T>` to fetch. The hook owns:
 *
 *   • an `AbortController` per request — its signal is handed to the fetcher and
 *     guards every state write, so a superseded or unmounted request can never
 *     clobber fresher state (replaces the ad-hoc `cancelled` boolean pattern),
 *   • a `refresh()` that re-runs the current fetcher.
 *
 * Callers MUST memoize `fetcher` (e.g. with `useCallback`) since its identity is
 * the effect dependency that triggers a refetch.
 */
export function useAsyncData<T>(
  fetcher: ((signal: AbortSignal) => Promise<T>) | null,
  options: UseAsyncDataOptions = {},
): UseAsyncDataResult<T> {
  const { errorMessage = 'The request failed.' } = options;

  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(fetcher !== null);
  const [error, setError] = useState<string | null>(null);
  const [refreshCount, setRefreshCount] = useState(0);

  const refresh = useCallback(() => {
    setRefreshCount((n) => n + 1);
  }, []);

  useEffect(() => {
    if (fetcher === null) {
      setData(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    fetcher(controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setData(result);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setData(null);
        setError(err instanceof Error ? err.message : errorMessage);
        setIsLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [fetcher, refreshCount, errorMessage]);

  return { data, isLoading, error, refresh };
}
